// Single-hole diagnostic page. The money screen for the demo — per-surface
// rootzone moisture heatmap, live sensor readings, and hole-scoped priorities
// colocated so the super can answer "what do I do on this hole today?" in one
// glance.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { Course, Hole, PlacedSensor, Zone, UserRole } from '@greeniq/shared';
import { api, ApiError } from '../api/client.js';
import { getSensorPlacements } from '../api/sensors.js';
import {
  getCourseHealth,
  getTodayPriorities,
  type CourseHealthResponse,
  type PriorityItem,
} from '../api/overview.js';
import { PageHeader } from '../components/ui/page-header';
import { StatTile } from '../components/ui/stat-tile';
import { DataCard } from '../components/ui/data-card';
import { Badge, type BadgeProps } from '../components/ui/badge';
import { HoleMap } from '../components/HoleMap.js';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  YAxis,
} from 'recharts';
import {
  DARK_CHART_TOKENS,
  darkTooltipContentStyle,
  darkTooltipItemStyle,
  darkTooltipLabelStyle,
} from '../lib/charts-theme';

interface CourseResponse {
  course: Course;
  holes: Hole[];
  zones: Zone[];
  role: UserRole;
}

const PRIORITY_VARIANT: Record<PriorityItem['priority'], BadgeProps['variant']> = {
  P1: 'destructive',
  P2: 'warning',
  P3: 'default',
};

export function HoleDetailPage() {
  const { courseSlug, holeNumber } = useParams();
  const [data, setData] = useState<CourseResponse | null>(null);
  const [placements, setPlacements] = useState<PlacedSensor[]>([]);
  const [health, setHealth] = useState<CourseHealthResponse | null>(null);
  const [priorities, setPriorities] = useState<PriorityItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const holeNum = Number.parseInt(holeNumber ?? '', 10);
  const hole = useMemo(
    () => data?.holes.find((h) => h.holeNumber === holeNum) ?? null,
    [data, holeNum],
  );
  const holeZones = useMemo(
    () => (hole ? (data?.zones ?? []).filter((z) => z.holeId === hole.id) : []),
    [data, hole],
  );
  const holeSensors = useMemo(
    () => placements.filter((s) => holeZones.some((z) => z.id === s.zoneId)),
    [placements, holeZones],
  );

  // Course bundle + placements — need zones to determine sensor filter.
  useEffect(() => {
    if (!courseSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const [resp, placementsResp] = await Promise.all([
          api.get<CourseResponse>(`/courses/${courseSlug}`),
          getSensorPlacements(courseSlug),
        ]);
        if (!cancelled) {
          setData(resp);
          setPlacements(placementsResp.placements);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  // Hole-scoped health + priorities kick off once we've resolved the hole id.
  useEffect(() => {
    if (!courseSlug || !hole) return;
    let cancelled = false;
    (async () => {
      try {
        const [h, p] = await Promise.all([
          getCourseHealth(courseSlug, 30, { holeId: hole.id }),
          getTodayPriorities(courseSlug, { holeId: hole.id }),
        ]);
        if (!cancelled) {
          setHealth(h);
          setPriorities(p.priorities);
        }
      } catch {
        /* soft-fail; page still renders */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug, hole]);

  if (error) {
    return (
      <div className="mx-auto max-w-6xl p-8">
        <div className="rounded-lg border border-[color:var(--color-error)]/30 bg-error-muted p-4 text-sm text-fg">
          {error}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mx-auto max-w-6xl p-8 text-sm text-fg-muted">Loading…</div>
    );
  }
  if (!hole) {
    return (
      <div className="mx-auto max-w-6xl p-8">
        <div className="rounded-lg border border-border bg-bg-card p-6 text-sm text-fg">
          No hole #{holeNum} on this course.
          <div className="mt-2">
            <Link to={`/app/${courseSlug}`} className="text-greeniq-400">
              ← Back to overview
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const holeAcres = (
    holeZones.reduce((s, z) => s + Number(z.areaSqft), 0) / 43560
  ).toFixed(2);
  const avgVwc = (() => {
    const vals = holeSensors.map((s) => s.latestVwc).filter((v): v is number => v != null);
    if (vals.length === 0) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  })();
  const healthScore = health?.score ?? null;

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10">
      <div className="mb-4">
        <Link
          to={`/app/${data.course.slug}`}
          className="inline-flex items-center gap-1.5 text-xs text-fg-muted transition-colors hover:text-greeniq-400"
        >
          <ArrowLeft size={12} /> {data.course.name}
        </Link>
      </div>

      <PageHeader
        eyebrow={`Hole ${hole.holeNumber}`}
        title={
          hole.par
            ? `Par ${hole.par}${hole.yardageWhite ? ` · ${hole.yardageWhite} yd` : ''}`
            : `Hole ${hole.holeNumber}`
        }
        description={[
          hole.grassGreen && `Green: ${hole.grassGreen}`,
          hole.grassFairway && `Fairway: ${hole.grassFairway}`,
          hole.grassTee && `Tee: ${hole.grassTee}`,
        ]
          .filter(Boolean)
          .join(' · ') || 'Grass species unknown'}
      />

      <div className="mt-8 grid gap-3 md:grid-cols-4">
        <StatTile label="Zones" value={String(holeZones.length)} />
        <StatTile label="Area" value={`${holeAcres} ac`} />
        <StatTile
          label="Sensors"
          value={String(holeSensors.length)}
        />
        <StatTile
          label="Avg VWC"
          value={avgVwc != null ? `${Math.round(avgVwc * 100)}%` : '—'}
        />
      </div>

      {/* The money visual: map with sensor-fused rootzone moisture overlay. */}
      <div className="mt-6">
        <HoleMap zones={holeZones} sensors={holeSensors} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {/* Today's priorities — scoped to this hole only. */}
        <DataCard
          title="Today on this hole"
          subtitle="Unacknowledged sensor alerts scoped to this hole"
          className="lg:col-span-2"
          glow
        >
          {priorities == null && (
            <div className="text-sm text-fg-muted">Loading…</div>
          )}
          {priorities != null && priorities.length === 0 && (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-fg-muted">
              All clear on Hole {hole.holeNumber}. No active sensor alerts.
            </div>
          )}
          {priorities != null && priorities.length > 0 && (
            <ol className="space-y-3">
              {priorities.map((p) => (
                <li
                  key={p.id}
                  className="rounded-md border border-border bg-bg-subtle p-3 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant={PRIORITY_VARIANT[p.priority]}>{p.priority}</Badge>
                    <span className="text-[10px] uppercase tracking-wide text-fg-muted">
                      {p.category}
                    </span>
                  </div>
                  <div className="mt-1 font-display font-semibold text-fg">
                    {p.title}
                  </div>
                  <div className="mt-0.5 text-fg-secondary">{p.detail}</div>
                  {p.action && (
                    <div className="mt-1 text-xs text-greeniq-400">→ {p.action}</div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </DataCard>

        {/* Hole-level health sparkline. */}
        <DataCard title="Hole health" subtitle="Composite NDVI · 30 days">
          {healthScore == null ? (
            <div className="text-sm text-fg-muted">No satellite data yet.</div>
          ) : (
            <>
              <div className="font-display text-4xl font-semibold text-greeniq-400">
                {healthScore}
                <span className="ml-1 text-xs uppercase tracking-wide text-fg-muted">
                  / 100
                </span>
              </div>
              {health && health.sparkline.length >= 2 && (
                <div className="mt-3 h-16">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={health.sparkline} margin={{ top: 2, right: 2, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="holeHealthGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={DARK_CHART_TOKENS.greenPrimary} stopOpacity={0.5} />
                          <stop offset="100%" stopColor={DARK_CHART_TOKENS.greenPrimary} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <YAxis domain={[0, 100]} hide />
                      <Tooltip
                        contentStyle={darkTooltipContentStyle}
                        itemStyle={darkTooltipItemStyle}
                        labelStyle={darkTooltipLabelStyle}
                        formatter={(v) => [`${v} / 100`, 'Health']}
                      />
                      <Area
                        type="monotone"
                        dataKey="score"
                        stroke={DARK_CHART_TOKENS.greenPrimary}
                        fill="url(#holeHealthGrad)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
              {health?.delta7d != null && (
                <div className="mt-2 text-xs text-fg-muted">
                  {health.delta7d > 0 ? '+' : ''}
                  {health.delta7d} vs 7d ago
                </div>
              )}
            </>
          )}
        </DataCard>
      </div>

      {/* Drill-down: zones within the hole. */}
      <h2 className="mt-10 font-display text-lg font-semibold text-fg">
        Zones on this hole
      </h2>
      <div className="mt-3 grid gap-2">
        {holeZones.map((z) => {
          const sensorsHere = holeSensors.filter((s) => s.zoneId === z.id);
          const zoneVwc = (() => {
            const vals = sensorsHere.map((s) => s.latestVwc).filter((v): v is number => v != null);
            if (vals.length === 0) return null;
            return vals.reduce((a, b) => a + b, 0) / vals.length;
          })();
          return (
            <Link
              key={z.id}
              to={`/app/${data.course.slug}/zones/${z.id}`}
              className="group flex items-center justify-between gap-3 rounded-md border border-border bg-bg-card px-4 py-3 text-sm transition-colors hover:border-greeniq-500/30"
            >
              <div className="flex min-w-0 items-center gap-3">
                <Badge variant="default" className="shrink-0 text-[10px]">
                  {z.zoneType.replace(/_/g, ' ')}
                </Badge>
                <span className="truncate text-fg">{z.name}</span>
              </div>
              <div className="flex shrink-0 items-center gap-4 text-xs text-fg-muted">
                <span>{Math.round(Number(z.areaSqft)).toLocaleString()} sqft</span>
                <span>
                  {sensorsHere.length} sensor{sensorsHere.length === 1 ? '' : 's'}
                </span>
                {zoneVwc != null && (
                  <span className="font-mono">
                    {Math.round(zoneVwc * 100)}% VWC
                  </span>
                )}
                <ArrowRight
                  size={14}
                  className="text-fg-muted transition-colors group-hover:text-greeniq-400"
                />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
